'use client';

import { useTranslations } from 'next-intl';
import { Box, Stack, Tooltip, Typography } from '@unity/cloud-ui';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';
import { Link } from '../link';

export interface GlossaryTermProps {
  term: string;
  definition?: string;
  moreInfoUrl?: string;
  displayTextOverride?: string;
}

export const GlossaryTerm = (props: GlossaryTermProps) => {
  const t = useTranslations();

  if (!props.term) {
    return null;
  }

  const tooltipContent = (
    <>
      <Typography component="span" fontWeight="bold">
        {props.term}
      </Typography>
      <Typography component="p">{props.definition}</Typography>
      {props.moreInfoUrl && (
        <Link href={props.moreInfoUrl} external>
          {t('mdx:glossary-term:more-info')}
        </Link>
      )}
    </>
  );

  return (
    <Tooltip arrow title={tooltipContent} placement="top">
      <Box component="div" sx={{ display: 'inline-block' }}>
        <Stack direction={'row'}>
          <Typography
            component="span"
            fontWeight="bold"
            sx={{ textDecoration: 'underline', width: 'max-content', cursor: 'help', display: 'flex' }}
            tabIndex={0}
          >
            {props.displayTextOverride || props.term}
          </Typography>
          <Typography sx={{ verticalAlign: 'super', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '-0.35rem' }}>
            {'?'}
          </Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
};

const documentationURL = getComponentDocumentationUrl('glossary-terms');

const descriptions = {
  src: 'The source path of the glossary term YAML file. The path must be prefixed with a leading slash character: / and must retain the extension .yaml. The locale of the linked glossary term will be inferred from the page that contains the reference.',
  termFileName:
    'The name of the glossary term file (without the .yaml extension) to load the definition from. The glossary term file will be looked up in the glossary folder of the current namespace and locale.',
  label:
    'The context from which to draw the definition of the term. A term can have different definitions based on the context where it is used, in which case the label is very important to distinguish those. Even if the term is unique enough where no other contexts are present in its definition, it is still required to define a label and use it.',
  displayTextOverride:
    'An override of the text over which the tooltip will be displayed. Common use case for this prop is to use it pluralize the glossary word. i.e Show the tooltip for `Child` when hovering the word `Children`',
  schema: `Display a glossary term with a definition. 
  
  
  **Example**
  
  \`\`\`mdx  
<GlossaryTerm
  termFileName="Child"
  displayTextOverride="Children"
  label="Demo"
/>
\`\`\`

${documentationURL}`,
};

const srcOnly = z.object({
  src: z.string(),
});

const termAndLabel = z.object({
  termFileName: z.string(),
  label: z.string(),
  displayTextOverride: z.string().optional(),
});

export const glossaryTermSchema = z.union([srcOnly, termAndLabel]).describe(descriptions.schema);
